import { AlfredSupportedOption, PaymentType } from "src/api";
import { usePaymentValidation } from "src/hooks";
import { _t } from "src/utils/translation";

interface UseDepositValidationParams {
  /** Raw fiat input (string) — drives Alfred-side limit validation */
  amount: string;
  /**
   * Stable-equivalent (USDC/USDT) of the fiat input, derived from the
   * Alfred quote. Drives platform-side limit and fee-after-amount
   * checks; pass 0 while the quote is still loading.
   */
  stableAmount: number;
  paymentFeeError: string | null;
  paymentFee?: number;
  selectedOption?: AlfredSupportedOption | null;
}

export interface DepositErrors {
  inputErrors: string[];
  paymentFeeErrors: string[];
  paymentValidationErrors: string[];
  alfredLimitErrors: string[];
}

const decimalsOf = (value: string): number => {
  const dot = value.indexOf(".");
  return dot === -1 ? 0 : value.length - dot - 1;
};

export const useDepositValidation = (params: UseDepositValidationParams) => {
  const { amount, stableAmount, paymentFeeError, paymentFee, selectedOption } =
    params;

  // Platform-side limits (deposit_min_amount, etc.) and fee-vs-amount
  // sanity must use the stable-equivalent — wallet currency is always
  // the route's stable side, never the source fiat.
  const { paymentValidationErrors } = usePaymentValidation({
    amount: stableAmount,
    paymentType: PaymentType.Deposit,
    fee: paymentFee,
  });

  const alfredLimitErrors: string[] = [];
  if (selectedOption && amount) {
    const numeric = Number(amount);
    const min = Number(selectedOption.min_amount);
    const max = Number(selectedOption.max_amount);
    if (Number.isFinite(numeric) && numeric > 0) {
      if (Number.isFinite(min) && numeric < min) {
        alfredLimitErrors.push(
          _t("VALIDATION.MINIMUM_AMOUNT", {
            paymentType: PaymentType.Deposit,
            minAmount: selectedOption.min_amount,
            currency: selectedOption.source_currency,
          })
        );
      }
      if (Number.isFinite(max) && numeric > max) {
        alfredLimitErrors.push(
          _t("VALIDATION.MAXIMUM_AMOUNT", {
            paymentType: PaymentType.Deposit,
            maxAmount: selectedOption.max_amount,
            currency: selectedOption.source_currency,
          })
        );
      }
    }
    // null `decimals` means "no constraint" per Alfred's schema
    if (
      selectedOption.decimals !== null &&
      selectedOption.decimals !== undefined
    ) {
      const allowed = Number(selectedOption.decimals);
      if (Number.isFinite(allowed) && decimalsOf(amount) > allowed) {
        alfredLimitErrors.push(
          _t("VALIDATION.AMOUNT_TOO_MANY_DECIMALS", { decimals: allowed })
        );
      }
    }
  }

  const errors: DepositErrors = {
    inputErrors: [
      ...(!amount ? [_t("VALIDATION.AMOUNT_SHOULD_NOT_BE_EMPTY")] : []),
      ...(Number(amount) <= 0 ? [_t("VALIDATION.AMOUNT_SHOULD_BE_GREATER_THAN_ZERO")] : []),
    ],
    paymentFeeErrors: paymentFeeError ? [_t("VALIDATION.ERROR_ESTIMATING_PAYMENT_FEE")] : [],
    paymentValidationErrors: paymentValidationErrors,
    alfredLimitErrors,
  };

  return {
    errors,
  };
};
